import React, { useState } from 'react';
import { ArrowRightIcon, CheckIcon, PhoneIcon } from 'lucide-react';
import { CTA_LABEL, PHONE_DISPLAY, PHONE_HREF } from './CtaButton';
interface LeadFields {
  name: string;
  business: string;
  phone: string;
  email: string;
}
const emptyLead: LeadFields = {
  name: '',
  business: '',
  phone: '',
  email: ''
};
export function StartSection() {
  const [fields, setFields] = useState<LeadFields>(emptyLead);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const update = (key: keyof LeadFields) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFields(prev => ({
      ...prev,
      [key]: event.target.value
    }));
    if (status === 'error') setStatus('idle');
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!fields.name.trim() || !fields.phone.trim() || !fields.email.trim()) {
      setStatus('error');
      return;
    }
    setStatus('submitting');
    window.setTimeout(() => setStatus('success'), 700);
  };
  return <section id="start" className="bg-brand-black text-brand-white" aria-labelledby="start-heading" data-id="element-143" data-mp="2l4tr8wj1cskk">
      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20 lg:py-24" data-id="element-144" data-mp="2g7bfyzhjq19e">
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] lg:gap-20" data-id="element-145" data-mp="2v3ix1qxnqmkg">
          <div data-id="element-146" data-mp="2q2mzdv3wfwnh">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red sm:text-xs" data-id="element-147" data-mp="2q23fi8uouy6t">
              Limited time — 72-hour launch
            </p>
            <h2 id="start-heading" className="mt-5 font-display text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl" data-id="element-148" data-mp="2z54iw1s66b6s">
              Start your 72-hour launch
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-brand-light" data-id="element-149" data-mp="38uxswycnyi8x">
              Tell us about your business and we will get your kickoff scheduled. Prefer to talk it
              through first? Call us — we answer.
            </p>

            <a href={PHONE_HREF} className="mt-8 inline-flex items-center gap-3 border-b-2 border-brand-red pb-1 font-display text-2xl font-extrabold tracking-tight transition-colors duration-150 ease-out hover:text-brand-red focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red sm:text-3xl" data-id="element-150" data-mp="3pw1ye79y4p8b">
              <PhoneIcon className="h-6 w-6 text-brand-red" aria-hidden="true" data-id="element-151" data-mp="cgvu95gx62nm" />
              {PHONE_DISPLAY}
            </a>
          </div>

          <div className="bg-brand-white p-7 text-brand-black sm:p-9" data-id="element-152" data-mp="he5iihsko89v">
            {status === 'success' ? <div className="flex h-full min-h-[18rem] flex-col justify-center" data-id="element-153" data-mp="mfl368qu8crq">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-brand-red" data-id="element-154" data-mp="rcl8uijrzdib">
                  <CheckIcon className="h-6 w-6 text-brand-white" aria-hidden="true" data-id="element-155" data-mp="2o80yz5q4on3p" />
                </span>
                <h3 className="mt-6 font-display text-2xl font-extrabold tracking-tight" data-id="element-156" data-mp="2t7dknn59jo4p">
                  You&apos;re in the queue.
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-brand-gray" data-id="element-157" data-mp="15j6kohb6e3bx">
                  We&apos;ll reach out at {fields.phone || 'the number you gave us'} within one
                  business day to kick off your 72-hour build. Need us sooner? Call{' '}
                  <a href={PHONE_HREF} className="font-semibold text-brand-black underline decoration-brand-red decoration-2 underline-offset-2" data-id="element-158" data-mp="1t90sgkgn11pb">
                    {PHONE_DISPLAY}
                  </a>
                  .
                </p>
              </div> : <form onSubmit={handleSubmit} noValidate data-id="element-159" data-mp="1f86ivyhusup2">
                <h3 className="font-display text-lg font-bold" data-id="element-160" data-mp="8t5wajepxov5">Request your kickoff</h3>

                <div className="mt-6 space-y-4" data-id="element-161" data-mp="8qt4lej5s04w">
                  <Field id="lead-name" label="Your name" value={fields.name} onChange={update('name')} autoComplete="name" data-id="element-162" data-mp="3t1e8oy5wm2t" />
                  <Field id="lead-business" label="Business name" value={fields.business} onChange={update('business')} autoComplete="organization" optional data-id="element-163" data-mp="1wjllvxzjq2p3" />
                  <Field id="lead-phone" label="Phone" type="tel" value={fields.phone} onChange={update('phone')} autoComplete="tel" data-id="element-164" data-mp="2bcncp6valg2u" />
                  <Field id="lead-email" label="Email" type="email" value={fields.email} onChange={update('email')} autoComplete="email" data-id="element-165" data-mp="26edlf7couzmi" />
                </div>

                {status === 'error' && <p role="alert" className="mt-4 text-sm font-semibold text-brand-red" data-id="element-166" data-mp="ihw8hg6rdd1j">
                    Please add your name, phone, and email so we can reach you.
                  </p>}

                <button type="submit" disabled={status === 'submitting'} className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-brand-red px-6 py-4 font-display text-sm font-bold uppercase tracking-wide text-brand-white transition-[background-color,transform] duration-150 ease-out hover:bg-[#9d1a1f] active:translate-y-px disabled:cursor-not-allowed disabled:opacity-70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red sm:text-base" data-id="element-167" data-mp="riu1l3fns05c">
                  {status === 'submitting' ? 'Sending…' : CTA_LABEL}
                  {status !== 'submitting' && <ArrowRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" aria-hidden="true" data-id="element-168" data-mp="1g6gehab0lup9" />}
                </button>

                <p className="mt-4 text-xs leading-relaxed text-brand-gray" data-id="element-169" data-mp="1pxkqc15e8zfv">
                  3-month minimum term. We&apos;ll confirm your package before anything is charged.
                </p>
              </form>}
          </div>
        </div>
      </div>
    </section>;
}
interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  autoComplete?: string;
  optional?: boolean;
}
function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  autoComplete,
  optional = false
}: FieldProps) {
  return <div data-id="element-170" data-mp="2mlqu6luhrnt3">
      <label htmlFor={id} className="block text-xs font-bold uppercase tracking-[0.12em] text-brand-gray" data-id="element-171" data-mp="2ml7kfsezw5q5">
        {label}
        {optional && <span className="ml-1 font-medium normal-case tracking-normal" data-id="element-172" data-mp="b0huiitqqb2q">(optional)</span>}
      </label>
      <input id={id} name={id} type={type} value={value} onChange={onChange} autoComplete={autoComplete} required={!optional} className="mt-2 w-full border-2 border-brand-light bg-brand-white px-3.5 py-3 text-sm text-brand-black transition-colors duration-150 ease-out placeholder:text-brand-gray focus:border-brand-black focus:outline-none" data-id="element-173" data-mp="6ywl57gf0exa" />
    </div>;
}